import { ClearFiltersButton } from "@/components/ui/ClearFiltersButton";
import { ErrorResult } from "@/components/ui/ErrorResult";
import { LoadingSection } from "@/components/ui/loadings/LoadingSection";
import { formatTime } from "@/helpers/formatDate";
import { formatPrice } from "@/helpers/formatPrice";
import useColumnSearch from "@/hooks/table/useColumnSearch";
import useTableFilters from "@/hooks/table/useTableFilters";
import { useClientPurchases } from "@/services/clients/queries";
import { Client, ClientPurchase } from "@/types/client";
import { useExportClientPurchase } from "@/views/private/clients/hook/useExportClientPurchase";
import { EyeOutlined } from "@ant-design/icons";
import { Button, Descriptions, Modal, Space, Table, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { DownloadIcon, CreditCardIcon } from "lucide-react";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { ModalVPayment, ModalVPaymentRef } from "../ModalVPayment";
import { paymentMethodLabel } from "@/views/private/PaymentsPrivate";
import { ModalPayClientCredit } from "./ModalPayClientCredit";

export const paymentStatusLabel: Record<
  string,
  { label: string; color: string }
> = {
  paid: { label: "Pagado", color: "green" },
  pending: { label: "Pendiente", color: "orange" },
};

export interface ModalVClientShoppingRef {
  childFunction: (client: Client) => void;
}

export const ModalVClientShopping = forwardRef<ModalVClientShoppingRef>(
  (props, ref) => {
    const [open, setOpen] = useState<boolean>(false);
    const [payCreditModalOpen, setPayCreditModalOpen] = useState<boolean>(false);
    const [id, setId] = useState<string>("");
    const [client, setClient] = useState<Client>();

    const { tableParams, tableKey, resetFilters, handleTableChange } = useTableFilters();

    const { data, isLoading, isFetching, isError, error, refetch } =
      useClientPurchases(id, tableParams);

    const { getColumnSearchProps } = useColumnSearch<ClientPurchase>();

    const modalVPayment = useRef<ModalVPaymentRef>(null);

    const childFunction = (client: Client) => {
      setOpen(true);
      setId(client.id);

      console.log(client);
      setClient(client);
      setTimeout(() => {
        void refetch();
      }, 0);
    };

    useImperativeHandle(ref, () => ({
      childFunction,
    }));

    const { generateExcel, contextHolder, loadingButtonExport } =
      useExportClientPurchase({
        customer: client as Client,
        tableParams,
      });

    const columns: ColumnsType<ClientPurchase> = [
      {
        title: "N° Ticket",
        dataIndex: "number_ticket",
        key: "number_ticket",
        render: (text: string) => (text ? text : <Tag color="red">Sin N°</Tag>),
      },
      {
        title: "Fecha",
        dataIndex: "created_at",
        key: "created_at",
        render: (text: string) => formatTime(text),
      },
      {
        title: "Total",
        dataIndex: "total",
        key: "total",
        render: (text: number) => formatPrice(text ?? 0),
      },
      {
        title: "Método de pago",
        dataIndex: "method",
        key: "method",
        render: (method) => {
          const config = paymentMethodLabel[method] ?? {
            label: method,
            color: "default",
          };

          return <Tag color={config.color}>{config.label}</Tag>;
        },
        filters: [
          {
            text: "Efectivo",
            value: "cash",
          },
          {
            text: "Tarjeta",
            value: "card",
          },
          {
            text: "Crédito",
            value: "credit",
          },
        ],
        filteredValue: tableParams.filters.method ?? null,
      },
      {
        title: "Estado de pago",
        dataIndex: "status",
        key: "status",
        render: (status) => {
          const config = paymentStatusLabel[status] ?? {
            label: status || "Pendiente",
            color: status === "paid" ? "green" : "orange",
          };

          return <Tag color={config.color}>{config.label}</Tag>;
        },
        filters: [
          {
            text: "Pagado",
            value: "paid",
          },
          {
            text: "Pendiente",
            value: "pending",
          },
        ],
        filteredValue: tableParams.filters.status ?? null,
      },
      {
        title: "N° Factura",
        dataIndex: "invoice_number",
        key: "invoice_number",
        ...getColumnSearchProps("invoice_number"),
        filteredValue: tableParams.filters.invoice_number ?? null,
        render: (text: string) => (text ? <Tag color="blue">{text}</Tag> : "-"),
      },
      {
        title: "Acciones",
        dataIndex: "id",
        key: "id",
        width: 100,
        render: (id) => (
          <Space size="middle">
            {/* EDIT */}
            <Tooltip
              placement="top"
              className="cursor-pointer"
              title="Detalles de compra"
            >
              <button
                onClick={() => {
                  modalVPayment.current?.childFunction(id);
                }}
              >
                <EyeOutlined className="text-lg" />
              </button>
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <Modal
        title="Compras de cliente"
        centered
        open={open}
        width={1000}
        onCancel={() => setOpen(false)}
        footer={<Button onClick={() => setOpen(false)}>Cerrar</Button>}
      >
        <ModalVPayment ref={modalVPayment} />

        {contextHolder}
        {isLoading || isFetching ? (
          <div className="flex pt-5">
            <LoadingSection />
          </div>
        ) : isError ? (
          <ErrorResult error={error} refetch={refetch} />
        ) : null}

        {!isError && !isLoading && !isFetching && (
          <>
            <div className="mb-5">
              <Descriptions
                layout="vertical"
                column={{
                  xs: 1,
                  sm: 1,
                  md: 2,
                  lg: 3,
                  xl: 3,
                  xxl: 3,
                }}
                size="middle"
              >
                <Descriptions.Item label="Tipo de cliente">
                  {client?.type === "person" ? "Persona" : "Empresa"}
                </Descriptions.Item>
                <Descriptions.Item label="Nombre">
                  {client?.name}
                </Descriptions.Item>
                <Descriptions.Item label="RUN">
                  {client?.tax_id ? (
                    client.tax_id
                  ) : (
                    <Tag color="red">Sin RUN</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Credito">
                  {"$" +
                    client?.credit_balance.toLocaleString("es-CL") +
                    "/" +
                    "$" +
                    client?.credit_limit.toLocaleString("es-CL")}
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* Acciones y Limpiar Filtros */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <ClearFiltersButton onClear={resetFilters} />

              <div className="flex justify-end space-x-2 ml-auto">
                {/* Boton para activar modo marcar como pagados */}
                <Button
                  className="flex items-center border-primary max-w-fit text-primary"
                  icon={<CreditCardIcon className={"size-5"} />}
                  onClick={() => setPayCreditModalOpen(true)}
                >
                  Bajar crédito (Facturar)
                </Button>
                <Button
                  loading={loadingButtonExport}
                  className="flex items-center border-primary max-w-fit text-primary"
                  icon={<DownloadIcon className={"size-5"} />}
                  onClick={() => generateExcel()}
                >
                  Descargar excel
                </Button>
              </div>
            </div>

            <Table<ClientPurchase>
              columns={columns}
              dataSource={data?.items}
              key={tableKey}
              pagination={{
                current: tableParams?.pagination.current,
                pageSize: tableParams?.pagination.pageSize,
                total: data?.total,
              }}
              onChange={handleTableChange}
              loading={isLoading || isFetching}
              scroll={{ x: 800 }}
            />

            {client && (
              <ModalPayClientCredit
                open={payCreditModalOpen}
                onCancel={() => setPayCreditModalOpen(false)}
                onSuccess={(newBalance) => {
                  if (typeof newBalance === "number") {
                    setClient((prev) =>
                      prev ? { ...prev, credit_balance: newBalance } : prev,
                    );
                  }
                  void refetch();
                }}
                client={client}
                purchases={data?.items || []}
              />
            )}
          </>
        )}
      </Modal>
    );
  },
);
